import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { useState } from "react";
import { Building2, Mail, Plus } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { dayjs } from "@boo/shared";
import { useGmailState } from "../store/useSettingsStore";
import { INPUT_CLASS } from "../types/settings.types";
import AccountNamesCard from "./AccountNamesCard";
import BankConfigRow from "./BankConfigRow";
import StatusBanner from "./StatusBanner";
import ConfirmModal from "@/components/ConfirmModal";

export default function GmailBanksTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    gmailStatus,
    gmailError,
    connecting,
    disconnecting,
    importing,
    importResult,
    bankConfigs,
    loadingConfigs,
    connect,
    disconnect,
    triggerImport,
    addBankConfig,
    saveBankConfig,
    removeBankConfig,
  } = useGmailState();

  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newPattern, setNewPattern] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newAccountNumber, setNewAccountNumber] = useState("");
  const [addingConfig, setAddingConfig] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  function dismissBanner() {
    setSearchParams({});
  }

  async function handleAddConfig(e: { preventDefault(): void }) {
    e.preventDefault();
    const label = newLabel.trim();
    const senderPattern = newPattern.trim();
    if (!label || !senderPattern) return;
    setAddingConfig(true);
    setAddError(null);
    try {
      await addBankConfig(
        label,
        senderPattern,
        newPassword,
        newAccountNumber.trim(),
      );
      setNewLabel("");
      setNewPattern("");
      setNewPassword("");
      setNewAccountNumber("");
      setShowAddForm(false);
    } catch {
      setAddError("Failed to add bank config");
    } finally {
      setAddingConfig(false);
    }
  }

  function handleCancelAdd() {
    setShowAddForm(false);
    setNewLabel("");
    setNewPattern("");
    setNewPassword("");
    setNewAccountNumber("");
  }

  return (
    <>
      <StatusBanner params={searchParams} onDismiss={dismissBanner} />
      {(gmailError || addError) && (
        <p className="text-sm text-red-500 mb-4">{gmailError ?? addError}</p>
      )}

      {/* Gmail Connection */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Gmail Connection
          </CardTitle>
        </CardHeader>
        <CardContent>
          {gmailStatus === null ? (
            <SkeletonRegion className="space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </SkeletonRegion>
          ) : gmailStatus.connected ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {gmailStatus.email}
                </p>
                {gmailStatus.connectedAt && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Connected{" "}
                    {dayjs(gmailStatus.connectedAt).format("D MMM YYYY")}
                  </p>
                )}
              </div>
              {importResult && (
                <p className="text-sm text-green-600 dark:text-green-400">
                  {importResult}
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={triggerImport}
                  loading={importing}
                >
                  {importing ? "Importing…" : "Import Now"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDisconnect(true)}
                  loading={disconnecting}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 border-red-200 dark:border-red-900"
                >
                  {disconnecting ? "Disconnecting…" : "Disconnect"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Connect a Gmail account to automatically import bank statement
                PDFs.
              </p>
              <Button size="sm" onClick={connect} loading={connecting}>
                {connecting ? "Redirecting…" : "Connect Gmail"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bank Configs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Bank Statement Configs
            </CardTitle>
            <button
              onClick={() => setShowAddForm((v) => !v)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Add bank config"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {showAddForm && (
            <form
              onSubmit={handleAddConfig}
              className="mb-4 space-y-2 pb-4 border-b border-border"
            >
              <div className="flex gap-2 flex-wrap">
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Bank name (e.g. KTC Credit Card)"
                  className={`flex-1 min-w-[160px] ${INPUT_CLASS}`}
                />
                <input
                  type="text"
                  value={newPattern}
                  onChange={(e) => setNewPattern(e.target.value)}
                  placeholder="Sender pattern (e.g. @ktc.co.th)"
                  className={`flex-1 min-w-[160px] ${INPUT_CLASS}`}
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="PDF password (leave blank if none)"
                  className={`flex-1 min-w-[160px] ${INPUT_CLASS}`}
                />
                <input
                  type="text"
                  value={newAccountNumber}
                  onChange={(e) => setNewAccountNumber(e.target.value)}
                  placeholder="Bank account number (optional — filters slips)"
                  className={`flex-1 min-w-[160px] ${INPUT_CLASS}`}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelAdd}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!newLabel.trim() || !newPattern.trim()}
                  loading={addingConfig}
                >
                  {addingConfig ? "Adding…" : "Add"}
                </Button>
              </div>
            </form>
          )}
          {loadingConfigs ? (
            <ListRowsSkeleton rows={2} />
          ) : bankConfigs.length === 0 && !showAddForm ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No bank configs yet. Add one to enable PDF password-protected
              imports.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {bankConfigs.map((config) => (
                <BankConfigRow
                  key={config.id}
                  config={config}
                  onDelete={setPendingDeleteId}
                  onSave={saveBankConfig}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AccountNamesCard />

      <ConfirmModal
        open={confirmDisconnect}
        title="Disconnect Gmail?"
        message="Future imports will stop until you reconnect."
        confirmLabel="Disconnect"
        danger
        onConfirm={() => {
          setConfirmDisconnect(false);
          disconnect();
        }}
        onCancel={() => setConfirmDisconnect(false)}
      />

      <ConfirmModal
        open={pendingDeleteId !== null}
        title="Delete bank config?"
        message="This bank config will be permanently removed."
        confirmLabel="Delete"
        danger
        onConfirm={() => {
          if (pendingDeleteId) removeBankConfig(pendingDeleteId);
          setPendingDeleteId(null);
        }}
        onCancel={() => setPendingDeleteId(null)}
      />
    </>
  );
}
